import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Upload Exams';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	feature: HubFeature.UploadExamsTool,
	portal: 'SP',
};

const htmlContent = `
<p>
    Follow the instructions below to format a CSV file that will allow you to upload credits manually 
    for candidates. Select the CSV file then press the Upload button to receive a CSV with any rows 
    that had errors.
</p>

<p><strong>To create the Comma Separated File (CSV) import file:</strong></p>

<p>
    Include the following column headers recognized by the parser. All fields are required for a
    successful upload. Extra fields will be ignored. Omitting the required columns will result in an
    error and the entire import will be aborted.
</p>

<p>Required Columns:</p>
<ul>
    <li>First Name</li>
    <li>Last Name</li>
    <li>Email</li>
    <li>Exam Code</li>
    <li>Exam Registration ID</li>
	<li>Exam Date</li>
    <li>Exam Grade
        <ul>
            <li>Must be 'Pass' or 'Fail'</li>
        </ul>
    </li>
</ul>

<p>Optional Columns:</p>
<ul>
    <li>Client ID
        <ul>
            <li>If this is not included, a new candidate record will be created.</li>
        </ul>
    </li>
</ul>

<p>
    Below the column header line, add a line for each candidate with their exam information. If the 
    <strong>Client ID</strong> is empty, a new candidate record will be created. The <strong>Exam Code</strong> 
    must match an exam catalog entry in the system. Non-matches will return an error. Only lines with errors 
    will be returned in a new CSV file.
</p>

<p><strong>Return CSV file:</strong></p>
<p>
    After pressing the Upload button, a CSV file will be returned with any errors. Successful imports will 
    not be returned in this file. The above columns will be returned in the CSV file. In addition, a "Message" 
    column will be added with details of the error. The Message column will contain one of the following text strings:
</p>

<ul>
    <li>ERROR: Missing name data.</li>
    <li>ERROR: New candidate data is missing a valid email address.</li>
    <li>ERROR: Missing/incomplete/invalid exam data.</li>
    <li>ERROR: ExamCode does not exist or is invalid.</li>
    <li>ERROR: No candidate exists with given Client ID. Either correct the ID, or remove it so it can be treated as a brand new candidate account.</li>
    <li>ERROR: Name information doesn't match existing candidate data.</li>
    <li>SKIPPED: Exam result already exists.</li>
</ul>
`;

export type SPUploadExamsLocNames =
	| 'SPUploadExamsPageTitle'
	| 'SPUploadExamsPageHeading'
	| 'SPUploadExamsButton'
	| 'SPUploadExamsInstructions'
	| 'SPUploadExamsParseError'
	| 'SPUploadExamsSuccessNotification';

export const spUploadExamsLocs: LocDefs<SPUploadExamsLocNames> = {
	SPUploadExamsPageTitle: {
		defaultValue: 'Manual Credit Upload',
		description: 'Page title for the upload exams page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPUploadExamsPageHeading: {
		defaultValue: 'Candidate Tools',
		description: 'Page heading for the upload exams page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPUploadExamsButton: {
		defaultValue: 'Upload',
		description: 'Button to upload exams',
		displayPath: `${dpRoot}|Upload Button`,
		displayType: 'Button',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPUploadExamsInstructions: {
		defaultValue: htmlContent,
		description: 'Instructions for the upload exams page',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		htmlAllowed: true,
		...sharedProperties,
	},
	SPUploadExamsParseError: {
		defaultValue:
			'Unable to parse the CSV file. Please ensure the file is formatted as per instructions.',
		description: 'Notification message when CSV parse fails',
		displayPath: `${dpRoot}|CSV parse error`,
		displayType: 'Error',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPUploadExamsSuccessNotification: {
		defaultValue: 'Exam upload successful',
		description: 'Notification message when exam upload is successful',
		displayPath: `${dpRoot}|Success Notification`,
		displayType: 'Notification',
		htmlAllowed: false,
		...sharedProperties,
	},
};

export const UPLOAD_EXAMS_LOC_KEYS = MapLocKeys<SPUploadExamsLocNames>(spUploadExamsLocs);
export type SPUploadExamsLocType = LocMap<SPUploadExamsLocNames>;
